import { action, computed, observable, set } from 'mobx';
import { isEmpty } from 'lodash';
import { ContainerBlock } from '~/models/blocks/container-block.model';
import {
    IPageInfoView,
    IPageResponse,
    IPageSubmission,
    ITransformedPageResponse
} from '~/types/server.types';
import { IContainerPageStyles } from '~/types/page-styles.types';
import type { RootStore } from '~/stores/root.store';
import { cloneClass } from '~/utils/data.utils';
import { ServiceItemScope, TBlock } from '~/types/builder.types';
import type { FlowRevision } from '~/models/flow-revision.model';
import type { InvoiceBlock } from '~/models/blocks/invoice-block.model';
import type { ServiceSelectionBlock } from '~/models/blocks/service-selection-block.model';
import { ServiceSelectionItemBlock } from '~/models/blocks/service-selection-item-block.model';
import { TPageActionBlocks } from '~/types/model.types';
import { ContractBlock } from '~/models/blocks/contract-block.model';
import type { ScheduleContainerBlock } from '~/models/blocks/schedule-container-block.model';

export type PageDataForServer = Pick<
    ITransformedPageResponse,
    | 'page_id'
    | 'order'
    | 'title'
    | 'root_block_id'
    | 'cta_text'
    | 'completed'
    | 'redirect_website_url'
>;

export class Page {
    @observable page_id: string;
    @observable flow_id: string;
    @observable flowRevision: FlowRevision<RootStore>;
    @observable title: string;
    @observable order: number;
    @observable root_block_id: string;
    @observable cta_text: string;
    @observable completed: boolean;
    @observable submissions: IPageSubmission[];
    @observable submitted_on: string;
    @observable views: IPageInfoView[];
    @observable redirect_website_url: string; // TODO: Change when website URL is active

    rootStore: RootStore;

    constructor(
        page: ITransformedPageResponse,
        flowRevision: FlowRevision<RootStore>,
        rootStore: RootStore
    ) {
        set(this, page);
        this.rootStore = rootStore;
        this.flowRevision = flowRevision;
    }

    @computed
    get indexInSortedPages() {
        return this.flowRevision.pages.findIndex(
            page => page.page_id === this.page_id
        );
    }

    @computed
    get rootBlock() {
        return this.flowRevision.blocksMap[
            this.root_block_id
        ] as ContainerBlock;
    }

    @computed
    get blocks(): TBlock[] {
        return this.rootBlock?.allDescendants().concat([this.rootBlock]);
    }

    @computed
    get flow() {
        return this.rootStore.flowStore.flows[this.flow_id];
    }

    @computed
    get isEmpty() {
        return !this.rootBlock || this.rootBlock.childBlocks.length === 0;
    }

    @computed
    get displayOrder() {
        return this.pageIndex + 1;
    }

    @computed
    get pageIndex() {
        return this.rootStore.builderUiStore.selectedFlow.pages.findIndex(
            page => page.page_id === this.page_id
        );
    }

    @computed
    get isBetweenScheduleAndPaymentPage() {
        const { pageWithPay, pageWithScheduler } = this.flow;

        if (!pageWithPay || !pageWithScheduler) {
            return false;
        }

        return (
            pageWithPay.order > this.order &&
            pageWithScheduler.order < this.order
        );
    }

    @computed
    get isLastPage() {
        return this.flowRevision.lastPageId === this.page_id;
    }

    @computed
    get isInvoicePage() {
        return !!this.invoiceContainerBlock;
    }

    @computed
    get isInvoiceOrPayPage() {
        return this.isInvoicePage || this.isPaymentPage;
    }
    get isInvoiceOrPay() {
        return this.isInvoiceOrPayPage;
    }

    get isSessionSelection() {
        return this.isSessionSelectionPage;
    }

    @computed
    get isContractPage() {
        return !!this.contractBlock;
    }

    @computed
    get isSessionSelectionPage() {
        return !!this.sessionSelectionBlock;
    }

    @computed
    get shouldTriggerScheduleOrderEducation() {
        return (
            this.isBetweenScheduleAndPaymentPage ||
            ((this.isSchedulePage || this.isPaymentPage) &&
                this.flow.paymentAfterScheduleGap > 1)
        );
    }

    @computed
    get blocksWithErrors() {
        const blocksWithErrors = this.rootBlock
            ?.allDescendants()
            .filter(block => {
                return block instanceof ServiceSelectionItemBlock
                    ? !isEmpty(
                          block.getErrors({ isDontCountSubItemsErrors: true })
                      )
                    : !isEmpty(block.errors);
            });

        if (this.isInvoicePage) {
            const viewOnlyServiceSelectionBlocksWithErrors =
                this.invoiceContainerBlock.invoiceServiceSelectionBlocks
                    .filter(block => !block.hasSelection)
                    .flatMap(serviceSelectionBlock =>
                        serviceSelectionBlock.allDescendants()
                    )
                    .filter(
                        block =>
                            block instanceof ServiceSelectionItemBlock &&
                            !isEmpty(
                                block.getErrors({
                                    scope: ServiceItemScope.Invoice
                                })
                            )
                    );

            return [
                ...blocksWithErrors,
                ...viewOnlyServiceSelectionBlocksWithErrors
            ];
        }
        return blocksWithErrors;
    }

    @computed
    get hasErrors() {
        return !!this.blocksWithErrors?.length;
    }

    @computed
    get numOfErrors() {
        return this.blocksWithErrors?.length;
    }

    isCompanyInfoBlockOnPaymentPage(location: 'bottom' | 'top') {
        if (!this.isPaymentPage) {
            return false;
        }

        const chargeBlock = this.blocks.find(bl => bl.isPayContainerBlock);

        return this.blocks.some((childBlock: TBlock) => {
            const isBlockBeforeOrAfterCharge =
                location === 'top'
                    ? childBlock.order < (chargeBlock?.order ?? 0)
                    : childBlock.order > (chargeBlock?.order ?? 100000);
            return (
                (childBlock as ContainerBlock).isCompanyInfoBlock &&
                isBlockBeforeOrAfterCharge
            );
        });
    }

    @computed
    get linkedFields() {
        return this.rootBlock.linkedFields;
    }

    @computed
    get background_image() {
        return this.rootBlock.background_image;
    }

    @computed
    get styles() {
        return this.rootBlock.styles as IContainerPageStyles;
    }

    @computed
    get invoiceContainerBlock() {
        return this.blocks?.find(
            block => block.isInvoiceContainerBlock
        ) as InvoiceBlock;
    }

    @computed
    get serviceSelectionBlocks() {
        return this.blocks.filter(
            block => block.isServiceSelectionBlock
        ) as ServiceSelectionBlock[];
    }

    @computed
    get sessionSelectionBlock() {
        return this.serviceSelectionBlocks.find(
            block => block.isSessionSelection
        );
    }

    @computed
    get hasSelectableServiceSelectionBlocks() {
        return this.serviceSelectionBlocks.some(block => block.hasSelection);
    }

    @computed
    get payContainerBlock() {
        return this.blocks.find(block => block.isPayContainerBlock);
    }

    @computed
    get scheduleContainerBlock() {
        return this.blocks?.find(
            block => block.isScheduleContainerBlock
        ) as ScheduleContainerBlock;
    }

    @computed
    get isSchedulePage() {
        return !!this.scheduleContainerBlock;
    }

    @computed
    get isLeadDetailsPage() {
        return !!this.blocks?.find(
            block => block.type === 'lead_details_block'
        );
    }

    @computed
    get contractBlock() {
        return this.blocks?.find(
            block => block.isContractBlock
        ) as ContractBlock;
    }

    @computed
    get isPaymentPage(): boolean {
        return !!this.payContainerBlock?.block_id;
    }

    @computed
    get actionBlock() {
        return this.blocks.find(
            block => block.isActionBlock
        ) as TPageActionBlocks;
    }

    @computed
    get isPageViewed() {
        return !!this.views.length;
    }

    @computed
    get shouldShowPageOverlay() {
        return (
            this.rootStore.builderUiStore.showPagesOverlayForId === this.page_id
        );
    }

    @computed
    get coupledBlocks() {
        return this.blocks.flatMap(block => block.coupledBlocks);
    }

    @action
    setOrder(order: number) {
        this.order = order;
    }

    @action
    setFlowRevision(flowRevision: FlowRevision<RootStore>) {
        this.flowRevision = flowRevision;
    }

    setCtaText(text) {
        this.cta_text = text;
    }

    isAfterPage(page: Page) {
        return this.order > page.order;
    }

    getDataForServer(): PageDataForServer {
        const {
            page_id,
            order,
            title,
            root_block_id,
            cta_text,
            completed,
            redirect_website_url // TODO: Change when website URL is active
        } = this;

        return {
            page_id,
            order,
            title,
            root_block_id,
            cta_text,
            completed,
            redirect_website_url // TODO: Change when website URL is active
        };
    }

    clone() {
        return cloneClass(this);
    }

    mergeAttributes(pageResponse: ITransformedPageResponse | IPageResponse) {
        set(this, pageResponse);
    }
}
